import {type RouteLocationStringParam, type RouteRecordRaw} from 'vue-router';
import {getNumericValue, toInt} from '../library/math';
import {redirectToPHPComponent} from './default';

const SettingsIndex        = () => import('../views/settings/SettingsIndex.vue');
const SettingsModule       = () => import('../views/settings/SettingsModule.vue');
const SettingsAddress      = () => import('../views/settings/SettingsAddress.vue');
const SettingsIntranet     = () => import('../views/settings/SettingsIntranet.vue');
const SettingsNotification = () => import('../views/settings/SettingsNotification.vue');

const SettingsPersonalDashboardPopup = () => import('../views/settings/SettingsPersonalDashboardPopup.vue');
const SettingsControl                = () => import('../views/settings/control/SettingsControl.vue');
const SettingsBackup                 = () => import('../views/settings/control/SettingsBackup.vue');
const SettingsLicense                = () => import('../views/settings/control/SettingsLicense.vue');

const SettingsUser                       = () => import('../views/settings/user/SettingsUser.vue');
const SettingsUserData                   = () => import('../views/settings/user/SettingsUserData.vue');
const SettingsUserLogin                  = () => import('../views/settings/user/SettingsUserLogin.vue');
const SettingsUserPassword               = () => import('../views/settings/user/SettingsUserPassword.vue');
const SettingsUserColors                 = () => import('../views/settings/user/SettingsUserColors.vue');
const SettingsUserNotification           = () => import('../views/settings/user/SettingsUserNotification.vue');
const SettingsUserMenu                   = () => import('../views/settings/user/SettingsUserMenu.vue');
const SettingsUserCalenderSync           = () => import('../views/settings/user/SettingsUserCalenderSync.vue');
const SettingsUserPersonalDashboardPopup = () => import('../views/settings/user/SettingsUserPersonalDashboardPopup.vue');
const SettingsUserModuleCustomer         = () => import('../views/settings/user/module/SettingsUserModuleCustomer.vue');
const SettingsUserModuleEmployee         = () => import('../views/settings/user/module/SettingsUserModuleEmployee.vue');
const SettingsUserModuleSessionservice   = () => import('../views/settings/user/module/SettingsUserModuleSessionservice.vue');

const SettingsExport            = () => import('../views/settings/export/SettingsExport.vue');
const SettingsExportDataPrivacy = () => import('../views/settings/export/SettingsExportDataPrivacy.vue');
const SettingsExportTwoClick    = () => import('../views/settings/export/SettingsExportTwoClick.vue');
const ExportList                = () => import('../views/export/ExportList.vue');

const SettingsLdap              = () => import('../views/settings/ldap/SettingsLdap.vue');
const SettingsLdapConfiguration = () => import('../views/settings/ldap/SettingsLdapConfiguration.vue');
const SettingsLdapEntityMapping = () => import('../views/settings/ldap/SettingsLdapEntityMapping.vue');
const SettingsLdapRoleMapping   = () => import('../views/settings/ldap/SettingsLdapRoleMapping.vue');
const SettingsLdapSync          = () => import('../views/settings/ldap/SettingsLdapSync.vue');

const SettingsTerminalList = () => import('../views/settings/terminal/SettingsTerminalList.vue');
const SettingsTerminalEdit = () => import('../views/settings/terminal/SettingsTerminalEdit.vue');
const SettingsTerminalView = () => import('../views/settings/terminal/SettingsTerminalView.vue');

const SettingsClientList = () => import('../views/settings/client/SettingsClientList.vue');
const SettingsClientEdit = () => import('../views/settings/client/SettingsClientEdit.vue');
const SettingsClientView = () => import('../views/settings/client/SettingsClientView.vue');
const SettingsAppNavigationAdd = () => import('../views/settings/SettingsAppNavigationEdit.vue');

const meta: MetaData = {
    module: 'settings',
};

let settings: RouteRecordRaw[] = [
    {
        path: '/settings/edit',
        name: 'settings-intranet',
        component: SettingsIntranet,
        meta,
    },
    {
        path: '/settings/modules',
        name: 'settings-modules',
        component: SettingsModule,
        meta,
    },
    {
        path: '/settings/address',
        name: 'settings-address',
        component: SettingsAddress,
        meta,
    },
    {
        path: '/settings/control',
        name: 'settings-control',
        redirect: {name: 'settings-control-backup'},
        component: SettingsControl,
        children: [
            {
                path: 'backup',
                name: 'settings-control-backup',
                component: SettingsBackup,
            },
            {
                path: 'license',
                name: 'settings-control-license',
                component: SettingsLicense,
            },
        ],
        meta,
    },
    {
        path: '/settings/ldap',
        name: 'settings-ldap',
        redirect: {name: 'settings-ldap-configuration'},
        component: SettingsLdap,
        children: [
            {
                name: 'settings-ldap-configuration',
                path: 'configuration',
                component: SettingsLdapConfiguration,
            },
            {
                name: 'settings-ldap-entity-mapping',
                path: 'entity-mapping',
                component: SettingsLdapEntityMapping,
            },
            {
                name: 'settings-ldap-role-mapping',
                path: 'role-mapping',
                component: SettingsLdapRoleMapping,
            },
            {
                name: 'settings-ldap-sync',
                path: 'user-sync',
                component: SettingsLdapSync,
            },
        ],
        meta,
    },
    {
        path: '/settings/personal-dashboard-popup',
        name: 'settings-personal-dashboard-popup',
        component: SettingsPersonalDashboardPopup,
        meta,
    },
    {
        path: '/settings/notification/:resource?',
        name: 'settings-notification',
        component: SettingsNotification,
        props(route: RouteLocationStringParam)
        {
            return {
                resource: route.params.resource,
            };
        },
        meta,
    },
    {
        path: '/settings/export',
        name: 'settings-export',
        redirect: {name: 'settings-export-data-privacy'},
        component: SettingsExport,
        children: [
            {
                name: 'settings-export-data-privacy',
                path: 'data-privacy',
                component: SettingsExportDataPrivacy,
            },
            {
                name: 'settings-export-two-click',
                path: 'two-click',
                component: SettingsExportTwoClick,
            },
            {
                name: 'settings-export-list',
                path: 'list',
                component: ExportList,
            },
        ],
        meta,
    },
    {
        path: '/settings/user',
        name: 'settings-user',
        redirect: {name: 'settings-user-data'},
        component: SettingsUser,
        children: [
            {
                name: 'settings-user-data',
                path: 'data',
                component: SettingsUserData,
            },
            {
                name: 'settings-user-login',
                path: 'login/:changed(changed)?',
                component: SettingsUserLogin,
                props(route: RouteLocationStringParam)
                {
                    return {
                        changed: 'undefined' !== typeof route.params.changed && route.params.changed === 'changed',
                    };
                },
            },
            {
                name: 'settings-user-password',
                path: 'password',
                component: SettingsUserPassword,
            },
            {
                name: 'settings-user-colors',
                path: 'colors',
                component: SettingsUserColors,
            },
            {
                name: 'settings-user-notification',
                path: 'notification/:resourceOrEvent?',
                component: SettingsUserNotification,
                props(route: RouteLocationStringParam)
                {
                    const param          = getNumericValue(route.params.resourceOrEvent);
                    let resource: string = '';
                    let event: number    = 0;

                    if ('number' === typeof param)
                    {
                        event = param;
                    }
                    else
                    {
                        resource = param;
                    }

                    return {
                        resource,
                        event,
                    };
                },
            },
            {
                name: 'settings-user-menu',
                path: 'menu',
                component: SettingsUserMenu,
            },
            {
                name: 'settings-user-calender-sync',
                path: 'calendar-sync',
                component: SettingsUserCalenderSync,
            },
            {
                name: 'settings-user-personal-dashboard-popup',
                path: 'personal-dashboard-popup',
                component: SettingsUserPersonalDashboardPopup,
            },
            {
                name: 'settings-user-module-customer',
                path: 'module/customer',
                component: SettingsUserModuleCustomer,
            },
            {
                name: 'settings-user-module-employee',
                path: 'module/employee',
                component: SettingsUserModuleEmployee,
            },
            {
                name: 'settings-user-module-meetingservice',
                path: 'module/meetingservice',
                component: SettingsUserModuleSessionservice,
            },
            {
                name: 'settings-user-module-councilservice',
                path: 'module/councilservice',
                component: SettingsUserModuleSessionservice,
            },
            {
                name: 'settings-user-change-login',
                path: 'change-login/hash/:hash',
                component: redirectToPHPComponent,
            },
        ],
        meta,
    },
    {
        path: '/useradministration/configuration/notification/event/:resourceOrEvent',
        redirect: {name: 'settings-user-notification'},
    },
    {
        path: '/useradministration/configuration/notification',
        redirect: {name: 'settings-user-notification'},
    },
    {
        path: '/useradministration/configuration/:wildcard*',
        redirect: {name: 'settings-user'},
    },
    {
        path: '/settings/terminal/list',
        name: 'settings-terminal',
        component: SettingsTerminalList,
        meta,
    },
    {
        path: '/settings/terminal/add',
        name: 'settings-terminal-add',
        component: SettingsTerminalEdit,
        props()
        {
            return {id: 0};
        },
        meta,
    },
    {
        path: '/settings/terminal/edit/:id(\\d+)',
        name: 'settings-terminal-edit',
        component: SettingsTerminalEdit,
        props(route: RouteLocationStringParam)
        {
            return {id: toInt(route.params.id)};
        },
        meta,
    },
    {
        path: '/settings/terminal/view/:id',
        name: 'settings-terminal-view',
        component: SettingsTerminalView,
        props(route: RouteLocationStringParam)
        {
            return {id: toInt(route.params.id)};
        },
        meta,
    },
    {
        path: '/settings/terminal/:wildcard*',
        redirect: {name: 'settings-terminal', params: {}},
    },
    {
        path: '/settings/client/list',
        name: 'settings-client',
        component: SettingsClientList,
        meta,
    },
    {
        path: '/settings/client/add',
        name: 'settings-client-add',
        component: SettingsClientEdit,
        props()
        {
            return {id: 0};
        },
        meta,
    },
    {
        path: '/settings/client/edit/:id',
        name: 'settings-client-edit',
        component: SettingsClientEdit,
        props(route: RouteLocationStringParam)
        {
            return {id: toInt(route.params.id)};
        },
        meta,
    },
    {
        path: '/settings/client/view/:id',
        name: 'settings-client-view',
        component: SettingsClientView,
        props(route: RouteLocationStringParam)
        {
            return {id: toInt(route.params.id)};
        },
        meta,
    },
    {
        path: '/settings/app-navigation/edit/:id?',
        name: 'settings-app-navigation-edit',
        component: SettingsAppNavigationAdd,
        props(route: RouteLocationStringParam)
        {
            return {id: toInt(route.params.id)};
        },
        meta,
    },
    {
        path: '/settings/client/:wildcard*',
        redirect: {
            name: 'settings-client',
            params: {},
        },
    },
    {
        path: '/settings',
        alias: '/settings/:wildcard*',
        name: 'settings',
        component: SettingsIndex,
        meta,
    },
];

export default settings;
